import { PageLoader } from '@/components/ui/loader';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Sparkles } from 'lucide-react';
import { AiRewriteMenu, useAiStatus } from '@/features/ai/ai';
import { apiPost } from '@/lib/api';
import { toast } from 'sonner';
import { BookOpen, ExternalLink, Eye, FolderPlus, Plus, Settings2, ThumbsUp, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Switch, Textarea } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useDebounce } from '@/hooks/use-debounce';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError } from '@/lib/api';
import { timeAgo } from '@/lib/format';
import { Markdown } from '@/lib/markdown';
import { cn } from '@/lib/utils';
import { useKbArticle, useKbArticles, useKbCategories, useKbMutations, useSaveSite, useSite, type KbCategory } from './api';

/**
 * The knowledge base (HubSpot Service Hub): articles in categories, published
 * to the public help centre or kept internal for the team.
 */
export function KnowledgeBasePage() {
  const { can } = usePermission();
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const debounced = useDebounce(q);
  const { data: categories = [] } = useKbCategories();
  const { data: articles = [], isLoading } = useKbArticles({ q: debounced || undefined, categoryId: categoryId || undefined });
  const { data: site } = useSite(can('marketing.site.view'));
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [category, setCategory] = useState<KbCategory | 'new' | null>(null);
  const [settings, setSettings] = useState(false);

  return (
    <div>
      <PageHeader
        title="Knowledge base"
        description="Help articles for customers and the team. Published ones appear in your help centre; internal ones only for staff."
        breadcrumbs={[{ label: 'Service' }, { label: 'Knowledge base' }]}
        actions={
          <div className="flex gap-2">
            {site?.helpCenterEnabled && (
              <Button variant="outline" asChild>
                <a href={site.helpCenterUrl} target="_blank" rel="noreferrer">
                  <ExternalLink /> Help centre
                </a>
              </Button>
            )}
            {can('marketing.site.update') && (
              <Button variant="outline" onClick={() => setSettings(true)}>
                <Settings2 /> Settings
              </Button>
            )}
            <Can permission="service.kb.create">
              <Button onClick={() => setEditing('new')}>
                <Plus /> New article
              </Button>
            </Can>
          </div>
        }
      />
      {site && !site.helpCenterEnabled && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          The public help centre is off. {can('marketing.site.update') ? 'Turn it on in Settings when your first articles are ready.' : 'An administrator can turn it on.'}
        </div>
      )}
      <div className="grid gap-5 lg:grid-cols-[15rem_1fr]">
        <div className="space-y-1">
          <button onClick={() => setCategoryId('')} className={cn('w-full rounded-lg px-3 py-2 text-start text-sm', !categoryId ? 'bg-primary/10 font-semibold text-primary' : 'hover:bg-accent')}>
            All articles
          </button>
          {categories.map((item) => (
            <div key={item.id} className="group flex items-center">
              <button
                onClick={() => setCategoryId(item.id)}
                className={cn('min-w-0 flex-1 truncate rounded-lg px-3 py-2 text-start text-sm', categoryId === item.id ? 'bg-primary/10 font-semibold text-primary' : 'hover:bg-accent')}
              >
                {item.name} <span className="text-xs text-muted-foreground">{item.articleCount}</span>
              </button>
              <Can permission="service.kb.update">
                <button onClick={() => setCategory(item)} className="hidden px-1 text-xs text-muted-foreground group-hover:block">
                  Edit
                </button>
              </Can>
            </div>
          ))}
          <Can permission="service.kb.create">
            <Button size="sm" variant="ghost" className="w-full justify-start" onClick={() => setCategory('new')}>
              <FolderPlus /> New category
            </Button>
          </Can>
        </div>
        <div className="space-y-3">
          <Input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Search articles…" />
          {isLoading ? (
            <PageLoader />
          ) : articles.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-10 text-center">
              <BookOpen className="mx-auto h-10 w-10 text-primary/60" />
              <p className="mt-3 font-semibold">No articles here</p>
              <p className="mt-1 text-sm text-muted-foreground">Start with your top five questions — how to order, delivery times, payment, returns, contact.</p>
            </div>
          ) : (
            articles.map((article) => (
              <Card key={article.id} className="cursor-pointer hover:shadow-md" onClick={() => setEditing(article.id)}>
                <CardContent className="flex flex-wrap items-start justify-between gap-3 pt-4">
                  <div className="min-w-0">
                    <p className="font-semibold">{article.title}</p>
                    <p className="line-clamp-1 text-sm text-muted-foreground">{article.excerpt}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {article.category?.name ?? 'No category'} · updated {timeAgo(article.updatedAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Eye className="h-3.5 w-3.5" /> {article.viewCount}
                    </span>
                    <span className="flex items-center gap-1">
                      <ThumbsUp className="h-3.5 w-3.5" /> {article.helpfulYes + article.helpfulNo ? Math.round((article.helpfulYes / (article.helpfulYes + article.helpfulNo)) * 100) : 0}%
                    </span>
                    {article.isInternal && <Badge variant="secondary">Internal</Badge>}
                    <Badge className={article.status === 'PUBLISHED' ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}>{article.status === 'PUBLISHED' ? 'Published' : 'Draft'}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
      {editing && <ArticleEditor id={editing === 'new' ? null : editing} categories={categories} defaultCategory={categoryId} onClose={() => setEditing(null)} />}
      {category && <CategoryDialog category={category === 'new' ? null : category} onClose={() => setCategory(null)} />}
      {settings && <HelpCenterSettings onClose={() => setSettings(false)} />}
    </div>
  );
}

function ArticleEditor({ id, categories, defaultCategory, onClose }: { id: string | null; categories: KbCategory[]; defaultCategory: string; onClose: () => void }) {
  const { data: article } = useKbArticle(id);
  const { saveArticle, removeArticle } = useKbMutations();
  const { can } = usePermission();
  const [form, setForm] = useState({ title: '', categoryId: defaultCategory, body: '', status: 'DRAFT' as 'DRAFT' | 'PUBLISHED', isInternal: false });
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (article) setForm({ title: article.title, categoryId: article.categoryId ?? '', body: article.body, status: article.status, isInternal: article.isInternal });
  }, [article]);

  const submit = async (status: 'DRAFT' | 'PUBLISHED') => {
    setError(null);
    try {
      await saveArticle.mutateAsync({ id: id ?? undefined, ...form, status, categoryId: form.categoryId || null });
      toast.success(status === 'PUBLISHED' ? 'Published' : 'Saved as draft');
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiRequestError ? failure.message : 'Could not save');
    }
  };
  const editable = id ? can('service.kb.update') : can('service.kb.create');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[94vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{id ? 'Edit article' : 'New article'}</DialogTitle>
          <DialogDescription>
            Markdown: <code># Heading</code>, <code>**bold**</code>, <code>- list</code>, <code>[link](https://…)</code>, <code>![image](https://…)</code>
          </DialogDescription>
        </DialogHeader>
        {id && !article ? (
          <PageLoader />
        ) : (
          <div className="space-y-3">
            <Input className="text-lg font-semibold" placeholder="Title" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
            <div className="flex flex-wrap items-center gap-3">
              <Select className="w-56" value={form.categoryId} onChange={(event) => setForm({ ...form, categoryId: event.target.value })}>
                <option value="">No category</option>
                {categories.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.isInternal} onCheckedChange={(value) => setForm({ ...form, isInternal: value })} /> Internal (team only)
              </label>
              {article?.url && (
                <a href={article.url} target="_blank" rel="noreferrer" className="text-sm text-primary underline">
                  View live
                </a>
              )}
            </div>
            <div className="flex gap-1 border-b">
              {(['write', 'preview'] as const).map((value) => (
                <button key={value} onClick={() => setTab(value)} className={cn('-mb-px border-b-2 px-3 py-1.5 text-sm capitalize', tab === value ? 'border-primary font-semibold text-primary' : 'border-transparent text-muted-foreground')}>
                  {value}
                </button>
              ))}
              <div className="ms-auto flex items-center gap-1 pb-1">
                <AiArticleButton title={form.title} onDraft={(body) => setForm((current) => ({ ...current, body }))} />
                <AiRewriteMenu text={form.body} onResult={(body) => setForm((current) => ({ ...current, body }))} />
              </div>
            </div>
            {tab === 'write' ? (
              <Textarea rows={18} className="font-mono text-sm" value={form.body} onChange={(event) => setForm({ ...form, body: event.target.value })} />
            ) : (
              <div className="min-h-[24rem] rounded-xl border p-5">
                <h1 className="text-2xl font-extrabold">{form.title}</h1>
                <Markdown source={form.body} className="mt-3 text-sm" />
              </div>
            )}
            <FormError message={error} />
          </div>
        )}
        <DialogFooter className={id ? 'sm:justify-between' : undefined}>
          {id && (
            <Can permission="service.kb.delete">
              <Button variant="ghost" className="text-destructive" onClick={() => window.confirm('Delete this article?') && removeArticle.mutate(id, { onSuccess: onClose })}>
                <Trash2 /> Delete
              </Button>
            </Can>
          )}
          {editable && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => submit('DRAFT')} disabled={saveArticle.isPending || form.title.trim().length < 3}>
                {form.status === 'PUBLISHED' ? 'Unpublish' : 'Save draft'}
              </Button>
              <Button onClick={() => submit('PUBLISHED')} disabled={saveArticle.isPending || form.title.trim().length < 3}>
                {form.status === 'PUBLISHED' ? 'Update' : 'Publish'}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ category, onClose }: { category: KbCategory | null; onClose: () => void }) {
  const { saveCategory, removeCategory } = useKbMutations();
  const [name, setName] = useState(category?.name ?? '');
  const [description, setDescription] = useState(category?.description ?? '');
  const [order, setOrder] = useState(category?.displayOrder ?? 0);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{category ? 'Edit category' : 'New category'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Name, e.g. Orders & delivery" value={name} onChange={(event) => setName(event.target.value)} />
          <Textarea rows={2} placeholder="Short description" value={description} onChange={(event) => setDescription(event.target.value)} />
          <div className="flex items-center gap-2">
            <Label>Order</Label>
            <Input type="number" className="w-24" value={order} onChange={(event) => setOrder(Number(event.target.value) || 0)} />
          </div>
        </div>
        <DialogFooter className={category ? 'sm:justify-between' : undefined}>
          {category && (
            <Button variant="ghost" className="text-destructive" onClick={() => window.confirm('Delete this category? Its articles stay, without a category.') && removeCategory.mutate(category.id, { onSuccess: onClose })}>
              <Trash2 /> Delete
            </Button>
          )}
          <Button
            disabled={name.trim().length < 2 || saveCategory.isPending}
            onClick={() =>
              saveCategory.mutate(
                { id: category?.id, name, description: description || null, displayOrder: order },
                { onSuccess: onClose, onError: (error) => toast.error(error instanceof ApiRequestError ? error.message : 'Could not save') },
              )
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HelpCenterSettings({ onClose }: { onClose: () => void }) {
  const { data: site } = useSite();
  const save = useSaveSite();
  const [form, setForm] = useState({ helpCenterName: '', helpCenterSlug: '', helpCenterColour: '#2563eb', helpCenterEnabled: false });
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (site) setForm({ helpCenterName: site.helpCenterName, helpCenterSlug: site.helpCenterSlug, helpCenterColour: site.helpCenterColour, helpCenterEnabled: site.helpCenterEnabled });
  }, [site]);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Help centre</DialogTitle>
          <DialogDescription>Your public help site, at dovocrm.com/help/{form.helpCenterSlug}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <Switch checked={form.helpCenterEnabled} onCheckedChange={(value) => setForm({ ...form, helpCenterEnabled: value })} /> Help centre is public
          </label>
          <div className="space-y-1">
            <Label>Name</Label>
            <Input value={form.helpCenterName} onChange={(event) => setForm({ ...form, helpCenterName: event.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Address</Label>
            <div className="flex items-center rounded-lg border ps-2 text-sm">
              <span className="text-muted-foreground">/help/</span>
              <input className="h-10 min-w-0 flex-1 bg-transparent px-1 outline-none" value={form.helpCenterSlug} onChange={(event) => setForm({ ...form, helpCenterSlug: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Colour</Label>
            <input type="color" className="h-10 w-full rounded-lg border" value={form.helpCenterColour} onChange={(event) => setForm({ ...form, helpCenterColour: event.target.value })} />
          </div>
          <FormError message={error} />
        </div>
        <DialogFooter>
          <Button
            disabled={!site || save.isPending}
            onClick={() =>
              site &&
              save.mutate(
                { domains: site.domains, ...form },
                { onSuccess: () => { toast.success('Saved'); onClose(); }, onError: (failure) => setError(failure instanceof ApiRequestError ? failure.message : 'Could not save') },
              )
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Drafts the article body from its title. */
function AiArticleButton({ title, onDraft }: { title: string; onDraft: (body: string) => void }) {
  const { data: status } = useAiStatus();
  const draft = useMutation({
    mutationFn: () => apiPost<{ body: string }>('/ai/article', { title, language: /[\u0600-\u06FF]/.test(title) ? 'ar' : 'en' }),
    onSuccess: (result) => onDraft(result.body),
    onError: (failure) => toast.error(failure instanceof ApiRequestError ? failure.message : 'The AI assistant could not answer'),
  });
  if (!status?.enabled) return null;
  return (
    <Button type="button" size="sm" variant="ghost" disabled={title.trim().length < 3 || draft.isPending} onClick={() => draft.mutate()}>
      {draft.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-primary" />} Draft with AI
    </Button>
  );
}
